import React, { useState, useEffect } from 'react';
import { TextInput, useInput } from 'react-admin';
import {
  Box,
  Typography,
  Alert,
  Chip,
  Button
} from '@mui/material';
import JsonEditorAce from '../../components/code/ace/json.editor';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';

interface JsonSchemaTextInputProps {
  source: string;
  label?: string;
  fullScreen?: boolean;
}

export const JsonSchemaTextInput: React.FC<JsonSchemaTextInputProps> = ({ 
  source, 
  label = "JSON Schema", 
  fullScreen = false 
}) => {
  const {
    field: { value, onChange },
    fieldState: { error, isDirty }
  } = useInput({ source });

  const [editorValue, setEditorValue] = useState('');
  const [isValid, setIsValid] = useState(true);
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (value) {
      try {
        // Parse and reformat for better display
        const parsed = typeof value === 'string' ? JSON.parse(value) : value;
        const formatted = JSON.stringify(parsed, null, 2);
        setEditorValue(formatted);
        setIsValid(true);
        setValidationError(null);
      } catch (error) {
        // If parsing fails, use raw value
        setEditorValue(typeof value === 'string' ? value : JSON.stringify(value));
        setIsValid(false);
        setValidationError('Invalid JSON format');
      }
    } else {
      // Default empty schema
      const defaultSchema = {
        "$schema": "http://json-schema.org/draft-07/schema#",
        "type": "object",
        "title": "New Schema",
        "description": "Schema definition",
        "properties": {
          "id": {
            "type": "string",
            "format": "uuid",
            "description": "Unique identifier"
          }
        },
        "required": ["id"],
        "additionalProperties": false
      };
      
      const formatted = JSON.stringify(defaultSchema, null, 2);
      setEditorValue(formatted);
      setIsValid(true);
      setValidationError(null);
    }
  }, [value]);

  const handleEditorChange = (newValue: any) => {
    try {
      let jsonString: string;
      
      if (typeof newValue === 'string') {
        // Validate that it's parseable JSON
        JSON.parse(newValue);
        jsonString = newValue;
      } else {
        jsonString = JSON.stringify(newValue, null, 2);
      }
      
      setEditorValue(jsonString);
      setIsValid(true);
      setValidationError(null);

      // This is the key: call React Admin's onChange to mark form as dirty
      onChange(jsonString);
      console.log('Updated JSON schema via React Admin onChange');
      
    } catch (error) {
      console.error('JSON validation error:', error);
      setIsValid(false);
      setValidationError(error.message);
      
      // Still update the display value and call onChange
      if (typeof newValue === 'string') {
        setEditorValue(newValue);
        onChange(newValue); // Let React Admin handle the invalid JSON
      }
    }
  };

  const formatJson = () => {
    try {
      const parsed = JSON.parse(editorValue);
      const formatted = JSON.stringify(parsed, null, 2);
      setEditorValue(formatted);
      onChange(formatted);
    } catch (error) {
      console.error('Cannot format invalid JSON');
    }
  };

  const getSchemaInfo = () => {
    if (!isValid) return null;
    
    try {
      const parsed = JSON.parse(editorValue);
      return {
        propertyCount: Object.keys(parsed.properties || {}).length,
        requiredCount: (parsed.required || []).length,
        hasDefinitions: !!(parsed.definitions && Object.keys(parsed.definitions).length > 0),
        hasExamples: !!(parsed.examples && parsed.examples.length > 0)
      };
    } catch {
      return null;
    }
  };

  const schemaInfo = getSchemaInfo();

  if (!fullScreen) {
    // Regular TextInput for basic form
    return (
      <TextInput 
        source={source}
        label={label}
        multiline
        rows={8}
        helperText="JSON Schema definition (use JSON Editor tab for full-screen editing)"
        style={{ fontFamily: 'monospace', fontSize: '12px' }}
      />
    );
  }

  // Full-screen JSON editor
  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      {/* Header with status and actions */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 2,
        p: 2,
        bgcolor: 'background.paper',
        borderRadius: 1
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6">
            {label}
          </Typography>
          
          {isValid ? (
            <Chip 
              icon={<CheckCircleIcon />}
              label="Valid JSON" 
              color="success" 
              size="small" 
            />
          ) : (
            <Chip 
              icon={<ErrorIcon />}
              label="Invalid JSON" 
              color="error" 
              size="small" 
            />
          )}
          
          {isDirty && (
            <Chip 
              label="Modified" 
              color="info" 
              size="small" 
            />
          )}
        </Box>

        <Button
          variant="outlined"
          startIcon={<FormatAlignLeftIcon />}
          onClick={formatJson}
          disabled={!isValid}
          size="small"
        >
          Format JSON
        </Button>
      </Box>

      {/* Schema info */}
      {schemaInfo && (
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Chip label={`${schemaInfo.propertyCount} properties`} variant="outlined" size="small" />
          <Chip label={`${schemaInfo.requiredCount} required`} variant="outlined" size="small" />
          {schemaInfo.hasDefinitions && <Chip label="Has definitions" variant="outlined" size="small" />}
          {schemaInfo.hasExamples && <Chip label="Has examples" variant="outlined" size="small" />}
        </Box>
      )}

      {/* Validation error */}
      {validationError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>JSON Syntax Error:</strong> {validationError}
          </Typography>
        </Alert>
      )}

      {/* React Admin form error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Form Error:</strong> {error.message}
          </Typography>
        </Alert>
      )}

      {/* Full-screen JSON Editor */}
      <Box sx={{ 
        height: '500px',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden'
      }}>
        <JsonEditorAce
          value={editorValue}
          onChange={handleEditorChange}
        />
      </Box>
    </Box>
  );
};
